import React, { useState, useEffect, useCallback } from 'react';
import { getDatabase, ref as dbRef, ref, push, get, set, onValue, query, orderByChild, equalTo } from "firebase/database";
import { getAuth } from 'firebase/auth';
import './AddPageComponent.css'; // Ensure you create the CSS file accordingly
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import DashboardHeader from '../AdminComponents/DashboardHeader';
import GenerativeOrbAnimation from './GenerativeOrbAnimation';
import PagesDisplay from './PagesDisplay';
import CreatePageModal from './CreatePageModal';
import BuyPremium from './BuyPremium';
import BottomPurchase from './BottomPurchase';
import PageWizard from './PageWizard';
import { 
    getUserUsername, 
    checkUsernameAvailability, 
    createUsername, 
    validateUsername,
    generateUsernameOptions 
} from '../utils/usernameHelpers';
import { getUserSpaces, createSpace, getSpacePages, updateSpacePageCount, getSpaceDesignSettings, deleteSpace, duplicateSpace } from '../utils/spaceHelpers';
import UsernameSetupModal from './UsernameSetupModal';
import SpaceOverviewCard from './SpaceOverviewCard';
import SpaceCreationWizard from './SpaceCreationWizard';

const AddPageComponent = () => {
    const { spaceId: routeSpaceId } = useParams(); // Get spaceId from URL if present
    const location = useLocation(); // Track URL changes for refresh detection
    const [showModal, setShowModal] = useState(false);
    const [pageName, setPageName] = useState('');
    const [pageRoute, setPageRoute] = useState('');
    const [customRoute, setCustomRoute] = useState('');
    const [pages, setPages] = useState([]);
    const [isRouteValid, setIsRouteValid] = useState(true);
    const [isPremium, setIsPremium] = useState(null);
    const [loading, setLoading] = useState(true);
    const [basicInfos, setBasicInfos] = useState(null);
    const [isTrialValid, setIsTrialValid] = useState(false);
    
    // Multi-Space System States
    const [userSpaces, setUserSpaces] = useState([]);
    const [selectedSpace, setSelectedSpace] = useState(null);
    const [showSpaceWizard, setShowSpaceWizard] = useState(false);
    const [isFirstTime, setIsFirstTime] = useState(false);
    const [isLoadingSpacePages, setIsLoadingSpacePages] = useState(false);
    const [hasProcessedRefresh, setHasProcessedRefresh] = useState(false);
    
    // Delete Modal States
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [spaceToDelete, setSpaceToDelete] = useState(null);
    const [deleteConfirmText, setDeleteConfirmText] = useState('');
    const [deleting, setDeleting] = useState(false);
    
    // Duplicate Modal States
    const [showDuplicateModal, setShowDuplicateModal] = useState(false);
    const [spaceToDuplicate, setSpaceToDuplicate] = useState(null);
    const [duplicateSpaceName, setDuplicateSpaceName] = useState('');
    const [duplicateUsername, setDuplicateUsername] = useState('');
    const [duplicating, setDuplicating] = useState(false);

    const db = getDatabase();
    const auth = getAuth();
    const currentUser = auth.currentUser;
    const navigate = useNavigate();

    // Load user's spaces (ohne Auto-Selection)
    const loadUserSpaces = async () => {
        if (!currentUser) return;
        
        try {
            const spaces = await getUserSpaces(currentUser.uid);
            setUserSpaces(spaces);
            setIsFirstTime(spaces.length === 0);
            
            console.log('✅ User spaces loaded:', spaces.length, 'spaces');
            
        } catch (error) {
            console.error('Error loading spaces:', error);
            setIsFirstTime(true);
        }
    };

    // Delete Space Handlers
    const handleDeleteSpaceClick = (space, e) => {
        e.stopPropagation(); // Prevent card navigation
        setSpaceToDelete(space);
        setDeleteConfirmText('');
        setShowDeleteModal(true);
    };

    const handleDeleteSpaceConfirm = async () => {
        if (!currentUser || !spaceToDelete) return;
        
        // Validate that the user entered the correct space name
        if (deleteConfirmText !== spaceToDelete.spaceName) {
            alert('Space name does not match. Please type the exact space name to confirm deletion.');
            return;
        }

        setDeleting(true);
        try {
            await deleteSpace(currentUser.uid, spaceToDelete.spaceId);
            await loadUserSpaces();
            console.log('✅ Space deleted:', spaceToDelete.spaceId);
            
            // Reset and close modal
            setShowDeleteModal(false);
            setSpaceToDelete(null);
            setDeleteConfirmText('');
        } catch (error) {
            console.error('❌ Error deleting space:', error);
            alert(`Error deleting space: ${error.message}`);
        } finally {
            setDeleting(false);
        }
    };

    const handleCancelDelete = () => {
        setShowDeleteModal(false);
        setSpaceToDelete(null);
        setDeleteConfirmText('');
    };

    // Duplicate Space Handlers
    const handleDuplicateSpaceClick = (space, e) => {
        e.stopPropagation(); // Prevent card navigation
        setSpaceToDuplicate(space);
        setDuplicateSpaceName(`${space.spaceName} Copy`);
        setDuplicateUsername(`${space.username}-copy`);
        setShowDuplicateModal(true);
    };

    const handleDuplicateSpaceConfirm = async () => {
        if (!currentUser || !spaceToDuplicate) return;
        
        // Validate inputs
        if (!duplicateSpaceName.trim() || !duplicateUsername.trim()) {
            alert('Please enter both space name and username.');
            return;
        }
        
        // Validate username format
        const validation = validateUsername(duplicateUsername);
        if (!validation.valid) {
            alert(`Invalid username: ${validation.error}`);
            return;
        }

        setDuplicating(true);
        try {
            const newSpace = await duplicateSpace(
                currentUser.uid,
                spaceToDuplicate.spaceId,
                duplicateSpaceName,
                duplicateUsername
            );
            
            await loadUserSpaces();
            console.log('✅ Space duplicated:', newSpace);
            
            // Reset and close modal
            setShowDuplicateModal(false);
            setSpaceToDuplicate(null);
            setDuplicateSpaceName('');
            setDuplicateUsername('');
            
            // Navigate to new space
            navigate(`/space/${newSpace.spaceId}`);
        } catch (error) {
            console.error('❌ Error duplicating space:', error);
            if (error.message === 'Username already taken') {
                alert('This username is already taken. Please try a different username.');
            } else {
                alert(`Error duplicating space: ${error.message}`);
            }
        } finally {
            setDuplicating(false);
        }
    };

    const handleCancelDuplicate = () => {
        setShowDuplicateModal(false);
        setSpaceToDuplicate(null);
        setDuplicateSpaceName('');
        setDuplicateUsername('');
    };

    // Load pages for selected space (with race condition protection)
    const loadSpacePages = useCallback(async (spaceId, source = 'unknown') => {
        if (!currentUser || !spaceId) return;
        
        // Prevent multiple concurrent calls
        if (isLoadingSpacePages) {
            console.log('⚠️ loadSpacePages already running, skipping:', source);
            return;
        }
        
        console.log('🔄 loadSpacePages starting from:', source);
        setIsLoadingSpacePages(true);
        
        try {
            const spacePages = await getSpacePages(currentUser.uid, spaceId);
            setPages(spacePages);
            console.log('✅ Space pages loaded:', spacePages.length, 'pages', `(${source})`);
            
            // Update page count in space data (without reloading spaces)
            await updateSpacePageCount(currentUser.uid, spaceId);
            
        } catch (error) {
            console.error('Error loading space pages:', error);
            setPages([]);
        } finally {
            setIsLoadingSpacePages(false);
        }
    }, [currentUser, isLoadingSpacePages]);

    const validateCustomRoute = (route) => {
        const isValid = /^[a-z0-9-]+$/.test(route); // Allow only lowercase letters, numbers, and hyphens
        setIsRouteValid(isValid);
        return isValid;
    };

    const checkCustomRouteExists = async (route) => {
        const publicPageRef = dbRef(db, `/publicPages/${route}`);
        const snapshot = await get(publicPageRef);
        return snapshot.exists();
    };

    const handleCreateClubPage = async () => {
        if (!currentUser) {
            console.error('No user is logged in.');
            return;
        }

        if (!customRoute.trim() || !validateCustomRoute(customRoute)) {
            setIsRouteValid(false);
            setTimeout(() => setIsRouteValid(true), 2000);
            return;
        }

        if (userSpaces.length === 0) {
            // Creating first space from input field
            const validation = validateUsername(customRoute);
            if (!validation.valid) {
                alert(`Invalid username: ${validation.error}`);
                return;
            }

            const isAvailable = await checkUsernameAvailability(customRoute);
            if (!isAvailable) {
                alert('Username is already taken. Please try a different name.');
                return;
            }

            // Create space first, then show page modal
            try {
                const newSpace = await createSpace(
                    currentUser.uid,
                    customRoute.charAt(0).toUpperCase() + customRoute.slice(1), // Capitalize first letter
                    customRoute
                );
                
                setSelectedSpace(newSpace);
                setUserSpaces([newSpace]);
                setCustomRoute(''); // Clear for page name input
                setShowModal(true); // Show modal for first page
            } catch (error) {
                alert(`Error creating space: ${error.message}`);
            }
        } else if (selectedSpace) {
            // Adding subpage to existing space
            const pageExists = await checkPageExists(selectedSpace.username, customRoute);
            if (pageExists) {
                alert('This page name is already in use in your space. Please try with a different name.');
            } else {
                setShowModal(true);
            }
        }
    };

    const checkPageExists = async (username, pageName) => {
        try {
            const db = getDatabase();
            const pageRef = ref(db, `/pages/${currentUser.uid}/${pageName}`);
            const snapshot = await get(pageRef);
            return snapshot.exists();
        } catch (error) {
            console.error('Error checking page existence:', error);
            return false;
        }
    };

    // Automatische Erstellung der ersten Page ohne Modal
    const createFirstPageAutomatically = async () => {
        if (!currentUser || !selectedSpace) {
            console.error('No user logged in or no space selected.');
            return;
        }

        console.log('🏠 Creating first page automatically...');

        const pageKey = "home";
        const pageSlug = "/"; // Root path
        
        const pageExists = await checkPageExists(selectedSpace.username, pageKey);
        if (pageExists) {
            console.log('Home page already exists in your space.');
            return;
        }

        // Load space design settings
        const spaceDesignSettings = await getSpaceDesignSettings(currentUser.uid, selectedSpace.spaceId);
        
        const newPage = {
            name: "Home",
            description: `Welcome to ${selectedSpace.spaceName}`,
            slug: pageSlug, // Root path "/"
            userId: currentUser.uid,
            spaceId: selectedSpace.spaceId,
            username: selectedSpace.username,
            isHomePage: true, // Always true for first page
            globalSettings: {
                font: spaceDesignSettings?.globalBigFont || "Voyage",
                smallFont: spaceDesignSettings?.globalSmallFont || "Jost",
                colorScheme: "SpaceInherited"
            },
            colors: spaceDesignSettings?.colors || {
                color1: "#CDD1C4",
                color2: "#5C80BC",
                color3: "#EBF5DF",
                color4: "#30323D",
                color5: "#FFFFFF"
            },
            backgroundColor: spaceDesignSettings?.backgroundColor || "#FFFFFF",
            widgets: [],
            seoSettings: {
                title: `Home - ${selectedSpace.spaceName}`,
                description: `Welcome to ${selectedSpace.spaceName}`,
                keywords: [],
                ogTitle: '',
                ogDescription: '',
                ogImage: ''
            },
            designInheritance: {
                inheritsFromSpace: true,
                spaceId: selectedSpace.spaceId,
                lastInheritedAt: new Date().toISOString()
            },
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
        };

        try {
            // Save home page
            const pageRef = dbRef(db, `/pages/${currentUser.uid}/${selectedSpace.spaceId}/${pageKey}`);
            await set(pageRef, newPage);
            console.log("✅ Home page created automatically");
            
            // Create URL mapping for root path
            const urlMappingRef = dbRef(db, `/urlMappings/${selectedSpace.username}/`);
            await set(urlMappingRef, { 
                pageId: `${currentUser.uid}/${selectedSpace.spaceId}/${pageKey}`,
                userId: currentUser.uid,
                spaceId: selectedSpace.spaceId,
                username: selectedSpace.username,
                isHomePage: true
            });
            
            console.log("✅ Home page URL mapping created automatically");
            await loadSpacePages(selectedSpace.spaceId, 'createFirstPageAutomatically');
            
            // Direkt zum Page Editor navigieren
            navigate(`/settings/page/${currentUser.uid}/${selectedSpace.spaceId}/${pageKey}`);
        } catch (error) {
            console.error("❌ Error creating home page automatically:", error);
            alert('Error creating home page. Please try again.');
        }
    };

    const createNewPage = async () => {
        if (!currentUser || !selectedSpace) {
            console.error('No user logged in or no space selected.');
            return;
        }

        // Determine if this is the first page (Home Page)
        const isFirstPage = pages.length === 0;
        
        if (isFirstPage) {
            // First page is always Home Page with root path
            const pageKey = "home";
            const pageSlug = "/"; // Root path
            
            const pageExists = await checkPageExists(selectedSpace.username, pageKey);
            if (pageExists) {
                alert('Home page already exists in your space.');
                return;
            }

            // Load space design settings
            const spaceDesignSettings = await getSpaceDesignSettings(currentUser.uid, selectedSpace.spaceId);
            
            const newPage = {
                name: pageName || "Home",
                description: `Welcome to ${selectedSpace.spaceName}`,
                slug: pageSlug, // Root path "/"
                userId: currentUser.uid,
                spaceId: selectedSpace.spaceId,
                username: selectedSpace.username,
                isHomePage: true, // Always true for first page
                globalSettings: {
                    font: spaceDesignSettings?.globalBigFont || "Voyage",
                    smallFont: spaceDesignSettings?.globalSmallFont || "Jost",
                    colorScheme: "SpaceInherited"
                },
                colors: spaceDesignSettings?.colors || {
                    color1: "#CDD1C4",
                    color2: "#5C80BC",
                    color3: "#EBF5DF",
                    color4: "#30323D",
                    color5: "#FFFFFF"
                },
                backgroundColor: spaceDesignSettings?.backgroundColor || "#FFFFFF",
                widgets: [],
                seoSettings: {
                    title: `${pageName || 'Home'} - ${selectedSpace.spaceName}`,
                    description: `Welcome to ${selectedSpace.spaceName}`,
                    keywords: [],
                    ogTitle: '',
                    ogDescription: '',
                    ogImage: ''
                },
                designInheritance: {
                    inheritsFromSpace: true,
                    spaceId: selectedSpace.spaceId,
                    lastInheritedAt: new Date().toISOString()
                },
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString()
            };

            // Save home page
            const pageRef = dbRef(db, `/pages/${currentUser.uid}/${selectedSpace.spaceId}/${pageKey}`);
            set(pageRef, newPage)
                .then(() => {
                    console.log("✅ Home page created successfully");
                    
                    // Create URL mapping for root path
                    const urlMappingRef = dbRef(db, `/urlMappings/${selectedSpace.username}/`);
                    return set(urlMappingRef, { 
                        pageId: `${currentUser.uid}/${selectedSpace.spaceId}/${pageKey}`,
                        userId: currentUser.uid,
                        spaceId: selectedSpace.spaceId,
                        username: selectedSpace.username,
                        isHomePage: true
                    });
                })
                .then(async () => {
                    console.log("✅ Home page URL mapping created");
                    await loadSpacePages(selectedSpace.spaceId, 'createNewPage');
                    
                    setPageName('');
                    setPageRoute('');
                    setShowModal(false);
                    navigate(`/settings/page/${currentUser.uid}/${selectedSpace.spaceId}/${pageKey}`);
                })
                .catch(error => {
                    console.error("❌ Error creating home page:", error);
                    alert('Error creating home page. Please try again.');
                });
                
            return; // Exit early for home page
        }
        
        // For sub pages (not home page) - use the provided route
        const pageSlug = pageRoute || pageName.toLowerCase().replace(/[^a-z0-9-]/g, '-').replace(/^-+|-+$/g, '');
        const pageKey = pageSlug;
        
        if (!pageSlug.trim() || !validateCustomRoute(pageSlug)) {
            alert("Please enter a valid page name.");
            return;
        }

        const pageExists = await checkPageExists(selectedSpace.username, pageKey);
        if (pageExists) {
            alert('This page name already exists in your space. Please try a different name.');
            return;
        }

        // Load space design settings for sub pages too
        const spaceDesignSettings = await getSpaceDesignSettings(currentUser.uid, selectedSpace.spaceId);
        
        const newPage = {
            name: pageName || "New Page",
            description: `${pageName} page for ${selectedSpace.spaceName}`,
            slug: pageSlug, // Normal slug for sub pages
            userId: currentUser.uid,
            spaceId: selectedSpace.spaceId,
            username: selectedSpace.username,
            isHomePage: false, // Never true for sub pages
            isActive: true, // Make page active by default
            globalSettings: {
                font: spaceDesignSettings?.globalBigFont || "Voyage",
                smallFont: spaceDesignSettings?.globalSmallFont || "Jost",
                colorScheme: "SpaceInherited"
            },
            colors: spaceDesignSettings?.colors || {
                color1: "#CDD1C4",
                color2: "#5C80BC",
                color3: "#EBF5DF",
                color4: "#30323D",
                color5: "#FFFFFF"
            },
            backgroundColor: spaceDesignSettings?.backgroundColor || "#FFFFFF",
            widgets: [],
            seoSettings: {
                title: `${pageName || 'New Page'} - ${selectedSpace.spaceName}`,
                description: `${pageName} page for ${selectedSpace.spaceName}`,
                keywords: [],
                ogTitle: '',
                ogDescription: '',
                ogImage: ''
            },
            designInheritance: {
                inheritsFromSpace: true,
                spaceId: selectedSpace.spaceId,
                lastInheritedAt: new Date().toISOString()
            },
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
        };

        // Save to new structure: /pages/{userId}/{spaceId}/{pageKey}
        const pageRef = dbRef(db, `/pages/${currentUser.uid}/${selectedSpace.spaceId}/${pageKey}`);
        set(pageRef, newPage)
            .then(() => {
                console.log("✅ New page created successfully in space:", selectedSpace.spaceId);
                console.log("📄 Page details:", { pageKey, slug: pageSlug, isHomePage: isFirstPage });
                
                // Create URL mapping for easy access
                const urlPath = isFirstPage ? "" : pageSlug; // Empty path for home page
                const urlMappingRef = dbRef(db, `/urlMappings/${selectedSpace.username}/${urlPath}`);
                return set(urlMappingRef, { 
                    pageId: `${currentUser.uid}/${selectedSpace.spaceId}/${pageKey}`,
                    userId: currentUser.uid,
                    spaceId: selectedSpace.spaceId,
                    username: selectedSpace.username,
                    isHomePage: isFirstPage
                });
            })
            .then(async () => {
                console.log("✅ URL mapping created successfully");
                
                // Reload space pages
                await loadSpacePages(selectedSpace.spaceId, 'createNewPage-final');
                
                setPageName('');
                setPageRoute('');
                setCustomRoute('');
                setShowModal(false);
                navigate(`/settings/page/${currentUser.uid}/${selectedSpace.spaceId}/${pageKey}`);
            })
            .catch(error => {
                console.error("❌ Error creating new page:", error);
                alert('Error creating page. Please try again.');
            });
    };

    useEffect(() => {
        if (currentUser) {
            setLoading(true); // Start loading
            
            // Load user's spaces first (only once)
            loadUserSpaces();
            
            // Kürzerer Emergency Fallback Timer für bessere User Experience
            const emergencyTimeout = setTimeout(() => {
                console.warn('EMERGENCY: AddPageComponent loading timeout - forcing fallback');
                setIsPremium(false);
                setIsTrialValid(false);
                setBasicInfos(false);
                setPages([]);
                setLoading(false);
            }, 6000); // Von 12 auf 6 Sekunden reduziert
            
            const userRef = dbRef(db, `/Users/${currentUser.uid}`);
            
            // Kürzerer Timeout für Firebase-Requests
            const timeoutPromise = new Promise((_, reject) => 
                setTimeout(() => reject(new Error('Firebase request timeout')), 3000) // Von 8 auf 3 Sekunden reduziert
            );
            
            const userDataPromise = get(userRef);
            
            // Pages werden jetzt space-spezifisch über loadSpacePages() geladen
            
            // Nur User-Daten laden - Pages werden space-spezifisch geladen
            Promise.allSettled([
                Promise.race([userDataPromise, timeoutPromise])
            ])
                .then((results) => {
                    const [userResult] = results;
                    
                    // User-Daten verarbeiten
                    if (userResult.status === 'fulfilled') {
                        const userInfo = userResult.value.val();
                        
                        if (!userResult.value.exists()) {
                            console.warn('User data does not exist yet');
                            setIsPremium(false);
                            setIsTrialValid(false);
                            setBasicInfos(false);
                        } else {
                            const basicInfosStatus = userInfo?.basicInfos ?? false;
                            setBasicInfos(basicInfosStatus);

                            const isPremiumUser = userInfo?.isPremium ?? false;
                            setIsPremium(isPremiumUser);

                            const trialStartDate = userInfo?.pagesFreeTrial?.startDate;
                            if (trialStartDate) {
                                const trialDate = new Date(trialStartDate);
                                const currentDate = new Date();
                                const diffInDays = Math.floor((currentDate - trialDate) / (1000 * 60 * 60 * 24));
                                setIsTrialValid(diffInDays < 30);
                            } else {
                                setIsTrialValid(false);
                            }
                        }
                    } else {
                        console.warn('User data request failed, using fallback values');
                        setIsPremium(false);
                        setIsTrialValid(false);
                        setBasicInfos(false);
                    }
                    
                    // Pages werden jetzt über loadSpacePages() geladen - nicht hier!
                    console.log('DEBUG: Skipping old pages loading system, using space-specific loading instead');
                    
                    clearTimeout(emergencyTimeout);
                    setLoading(false);
                    console.log('AddPageComponent loading completed successfully');
                })
                .catch(error => {
                    console.error("Error fetching data:", error);
                    clearTimeout(emergencyTimeout);
                    
                    // Fallback-Werte setzen
                    setIsPremium(false);
                    setIsTrialValid(false);
                    setBasicInfos(false);
                    setPages([]);
                    setLoading(false);
                });
        } else {
            setLoading(false);
        }
    }, [currentUser, db, navigate]);

    // Handle route-based space selection (separate from loading)
    useEffect(() => {
        if (!currentUser || userSpaces.length === 0) return;
        
        console.log('🔄 Route effect:', { routeSpaceId, currentSelection: selectedSpace?.spaceId });
        
        if (routeSpaceId) {
            // URL has spaceId - select that space if not already selected
            if (!selectedSpace || selectedSpace.spaceId !== routeSpaceId) {
                const targetSpace = userSpaces.find(space => space.spaceId === routeSpaceId);
                if (targetSpace) {
                    console.log('🎯 Selecting space from URL:', routeSpaceId);
                    setSelectedSpace(targetSpace);
                    loadSpacePages(routeSpaceId, 'route-based-selection');
                } else {
                    console.log('⚠️ Space not found, redirecting to overview');
                    navigate('/pages');
                }
            }
        } else {
            // URL has no spaceId - ensure we show overview
            if (selectedSpace) {
                console.log('🏠 Clearing selection for overview');
                setSelectedSpace(null);
                setPages([]);
            }
        }
    }, [routeSpaceId, userSpaces, currentUser]); // Entfernt selectedSpace aus dependencies!

    // Listen for URL refresh parameter to reload pages after settings save (ONE TIME ONLY)
    useEffect(() => {
        if (!selectedSpace || !currentUser || hasProcessedRefresh || isLoadingSpacePages) return;
        
        const urlParams = new URLSearchParams(location.search);
        const refreshParam = urlParams.get('refresh');
        
        if (refreshParam) {
            console.log('🔄 Detected refresh parameter, reloading space pages...');
            setHasProcessedRefresh(true); // Prevent infinite loop
            
            // Clean up URL immediately
            const newUrl = `${location.pathname}`;
            window.history.replaceState({}, '', newUrl);
            
            // Only reload if not already loading
            if (!isLoadingSpacePages) {
                setTimeout(() => {
                    loadSpacePages(selectedSpace.spaceId, 'refresh-parameter');
                }, 150); // Increased delay to avoid race conditions
            }
        }
    }, [location.search, selectedSpace, currentUser, hasProcessedRefresh, isLoadingSpacePages, loadSpacePages]);

    // Reset refresh flag when space changes
    useEffect(() => {
        setHasProcessedRefresh(false);
    }, [selectedSpace?.spaceId]);

    // Block body scroll when Space Wizard is open
    useEffect(() => {
        if (showSpaceWizard) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'auto';
        }
        
        // Cleanup on unmount
        return () => {
            document.body.style.overflow = 'auto';
        };
    }, [showSpaceWizard]);

    // Animation Variants entfernt

    if (loading) {
        return (
            <div className="loading-spinner" id="fake-loading-spinner">
                <h1 style={{ marginTop: '-100px' }}>BonusClub</h1>
                <div className="spinner-circle"></div>
            </div>
        ); // Show spinner while loading is true
    }

    if (!isPremium && !isTrialValid) {
        return <BuyPremium />;
    }

    return (
        <div>
          <BottomPurchase />
            <DashboardHeader />
            <GenerativeOrbAnimation />

            <div className="add-page-container">
                <>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', zIndex: '100', justifyContent: 'center', marginTop: '0px', width: '100%' }}>
                        {!selectedSpace ? (
                            // LAYER 1: Space-Übersicht
                            <div className="spaces-overview">
                                <div className="salespage-spaces-header">
                                    <div className="salespage-ai-header-container">
                                        <div className="salespage-ai-icon-container">
                                            <img src="/img/salesgod_dark.webp" alt="SalesGod" className="salespage-ai-brain-icon" style={{ width: '58px', height: '58px' }} />
                                            <div className="salespage-ai-pulse-ring"></div>
                                        </div>
                                        <h2 className="salespage-ai-title">
                                            <span className="salespage-sales-text"></span>
                                            <span className="salespage-ai-text">
                                                SalesPages
                                                <div className="salespage-ai-glow"></div>
                                            </span>
                                        </h2>
                                        <p className="salespage-ai-subtitle">
                                            Manage and create SalesPages with your own domain and AI-powered features.
                                        </p>
                                        <div className="salespage-ai-feature-badges">
                                            <span className="salespage-feature-badge">
                                                <i className="fas fa-bolt"></i>
                                                Lightning Fast
                                            </span>
                                            <span className="salespage-feature-badge">
                                                <i className="fas fa-wand-magic-sparkles"></i>
                                                AI Design
                                            </span>
                                            <span className="salespage-feature-badge">
                                                <i className="fas fa-network-wired"></i>
                                                Multi-Domain
                                            </span>
                                        </div>
                                        
                                        <div className="salespage-cta-container">
                                            <div 
                                                className="salespage-cta-content"
                                                onClick={() => setShowSpaceWizard(true)}
                                            >
                                                <div className="salespage-cta-text">
                                                    Create a new Sales Page for my Business...
                                                </div>
                                                <button 
                                                    className="salespage-cta-btn"
                                                    onClick={(e) => {
                                                        e.stopPropagation(); // Verhindert doppeltes Triggern
                                                        setShowSpaceWizard(true);
                                                    }}
                                                >
                                                    <i className="fas fa-arrow-right"></i>
                                                    Create with AI
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                    
                                    {/* Space Cards Grid direkt im Header */}
                                    {userSpaces.length > 0 && (
                                        <div className="spaces-grid" style={{ marginTop: '40px' }}>
                                            {/* Add New Space Card - Now at the beginning */}
                                            <div 
                                                className="space-card add-space-card"
                                                onClick={() => setShowSpaceWizard(true)}
                                            >
                                                <div className="add-space-content">
                                                    <i className="fas fa-plus"></i>
                                                    <h3>Add New Space</h3>
                                                    <p>Create another website</p>
                                                </div>
                                            </div>
                                            
                                            {userSpaces.filter(space => space && space.spaceId).map(space => (
                                                <div 
                                                    key={space.spaceId} 
                                                    className="space-card"
                                                    onClick={() => {
                                                        console.log('🎯 Navigating to space:', space.spaceId);
                                                        navigate(`/space/${space.spaceId}`);
                                                    }}
                                                >
                                                    <div className="space-card-header">
                                                        <h3>{space.spaceName || 'Unnamed Space'}</h3>
                                                        <div className="space-urls">
                                                            <span>bonusclub.link/{space.username || space.spaceId}/</span>
                                                            {space.customDomain && (
                                                                <span className="custom-domain">{space.customDomain}</span>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <div className="space-stats">
                                                        <span>{space.pageCount || 0} Pages</span>
                                                        {space.domainStatus === 'none' ? (
                                                            <a 
                                                                href={`https://bonusclub.link/to/${space.username}/`}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="domain-status domain-link bonusclub-link"
                                                                onClick={(e) => e.stopPropagation()}
                                                            >
                                                                <i className="fas fa-external-link-alt"></i>
                                                                BonusClub Link
                                                            </a>
                                                        ) : (space.domainStatus === 'verified' || space.domainStatus === 'active') && space.customDomain ? (
                                                            <a 
                                                                href={`https://${space.customDomain}`}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="domain-status domain-link custom-domain-link"
                                                                onClick={(e) => e.stopPropagation()}
                                                            >
                                                                <i className="fas fa-external-link-alt"></i>
                                                                Visit Salespage
                                                            </a>
                                                        ) : (
                                                            <span className={`domain-status ${space.domainStatus}`}>
                                                                {space.domainStatus}
                                                            </span>
                                                        )}
                                                    </div>
                                                    
                                                    {/* Action Buttons */}
                                                    <div className="space-card-actions">
                                                        <button 
                                                            className="space-card-duplicate-icon-btn"
                                                            onClick={(e) => handleDuplicateSpaceClick(space, e)}
                                                            title="Duplicate Space"
                                                        >
                                                            <i className="fas fa-copy"></i>
                                                        </button>
                                                        <button 
                                                            className="space-card-delete-icon-btn"
                                                            onClick={(e) => handleDeleteSpaceClick(space, e)}
                                                            title="Delete Space"
                                                        >
                                                            <i className="fas fa-trash"></i>
                                                        </button>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>

                            </div>
                        ) : (
                            // LAYER 2: Page-Management für ausgewählten Space
                            <div className="space-management">
                                <SpaceOverviewCard
                                    userUsername={selectedSpace.username}
                                    spaceId={selectedSpace.spaceId}
                                    spaceName={selectedSpace.spaceName}
                                    pages={pages}
                                    activePageIndex={selectedSpace.activePageIndex ?? (pages.length > 0 ? pages.length - 1 : 0)}
                                    setActivePageIndex={(index) => {
                                        console.log('🔄 AddPageComponent: Setting activePageIndex to:', index);
                                        setUserSpaces(prevSpaces => 
                                            prevSpaces.map(space => 
                                                space.spaceId === selectedSpace.spaceId 
                                                    ? { ...space, activePageIndex: index }
                                                    : space
                                            )
                                        );
                                        // Auch selectedSpace direkt aktualisieren
                                        setSelectedSpace(prev => ({ ...prev, activePageIndex: index }));
                                    }}
                                    onAddPage={() => {
                                        // Bei der ersten Page automatisch erstellen, sonst Modal zeigen
                                        if (pages.length === 0) {
                                            createFirstPageAutomatically();
                                        } else {
                                            setShowModal(true);
                                        }
                                    }}
                                    onSpaceDeleted={() => {
                                        console.log('🗑️ Space deleted, navigating to overview');
                                        navigate('/pages');
                                    }}
                                    onBackToSpaces={() => {
                                        console.log('🏠 Navigating back to spaces overview');
                                        navigate('/pages');
                                    }}
                                />
                                
                                {/* Show PagesDisplay only when in a space with pages */}
                                {pages.length > 0 && (
                                    <PagesDisplay 
                                        pages={pages} 
                                        setPages={setPages}
                                        activePageIndex={selectedSpace.activePageIndex ?? (pages.length > 0 ? pages.length - 1 : 0)}
                                        setActivePageIndex={(index) => {
                                            console.log('🔄 AddPageComponent (PagesDisplay): Setting activePageIndex to:', index);
                                            setUserSpaces(prevSpaces => 
                                                prevSpaces.map(space => 
                                                    space.spaceId === selectedSpace.spaceId 
                                                        ? { ...space, activePageIndex: index }
                                                        : space
                                                )
                                            );
                                            // Auch selectedSpace direkt aktualisieren
                                            setSelectedSpace(prev => ({ ...prev, activePageIndex: index }));
                                        }}
                                    />
                                )}
                            </div>
                        )}
                    </div>
                    {pages.length === 0 && (
                        <div className="no-pages-message-2">
                            <img className="val-pic" src="/img/val.webp" alt="" />
                            <a style={{ textDecoration: 'none', color: 'black' }} href="https://bonusclub.io" target="_blank">
                                <p>Need help? 🤗 <br /> <span>Click here to get support</span></p>
                            </a>
                        </div>
                    )}
                </>
                <CreatePageModal
                    showModal={showModal}
                    setShowModal={setShowModal}
                    pageName={pageName}
                    setPageName={setPageName}
                    pageRoute={pageRoute}
                    setPageRoute={setPageRoute}
                    createNewPage={createNewPage}
                    userUsername={selectedSpace?.username || ''}
                />
                
                {/* Space Creation Wizard */}
                {showSpaceWizard && (
                    <div className="space-wizard-overlay">
                        <SpaceCreationWizard
                            onComplete={(newSpace) => {
                                console.log('🎯 AddPageComponent received newSpace:', newSpace);
                                setShowSpaceWizard(false);
                                
                                if (newSpace && newSpace.spaceId) {
                                    setUserSpaces(prev => [...prev, newSpace]);
                                    setSelectedSpace(newSpace);
                                    loadSpacePages(newSpace.spaceId, 'space-wizard-creation');
                                } else {
                                    console.error('❌ newSpace is undefined or missing spaceId:', newSpace);
                                }
                                
                                // Re-enable body scroll
                                document.body.style.overflow = 'auto';
                            }}
                            onCancel={() => {
                                setShowSpaceWizard(false);
                                // Re-enable body scroll
                                document.body.style.overflow = 'auto';
                            }}
                        />
                    </div>
                )}

                {/* Duplicate Space Modal */}
                {showDuplicateModal && spaceToDuplicate && (
                    <div className="space-card-duplicate-modal-overlay">
                        <div className="space-card-duplicate-modal">
                            <div className="space-card-duplicate-header">
                                <h3>
                                    <i className="fas fa-copy"></i>
                                    Duplicate Space
                                </h3>
                                <button 
                                    className="close-modal-btn"
                                    onClick={handleCancelDuplicate}
                                    disabled={duplicating}
                                >
                                    <i className="fas fa-times"></i>
                                </button>
                            </div>
                            
                            <div className="space-card-duplicate-body">
                                <p className="space-card-duplicate-description">
                                    Create a copy of <strong>{spaceToDuplicate.spaceName}</strong> with all pages and design settings. 
                                    Domain settings will not be copied.
                                </p>

                                <div className="space-card-duplicate-info-box">
                                    <div className="duplicate-info-item">
                                        <i className="fas fa-check-circle"></i>
                                        <span><strong>{spaceToDuplicate.pageCount || 0}</strong> Pages will be copied</span>
                                    </div>
                                    <div className="duplicate-info-item">
                                        <i className="fas fa-palette"></i>
                                        <span>Design settings will be copied</span>
                                    </div>
                                    <div className="duplicate-info-item">
                                        <i className="fas fa-times-circle"></i>
                                        <span>Domain settings will NOT be copied</span>
                                    </div>
                                </div>

                                <div className="form-group space-card-duplicate-input-group">
                                    <label>
                                        Space Name
                                    </label>
                                    <input
                                        type="text"
                                        value={duplicateSpaceName}
                                        onChange={(e) => setDuplicateSpaceName(e.target.value)}
                                        placeholder="Enter space name"
                                        disabled={duplicating}
                                        className="space-card-duplicate-input"
                                        autoFocus
                                    />
                                </div>

                                <div className="form-group space-card-duplicate-input-group">
                                    <label>
                                        Username / Route
                                    </label>
                                    <div className="space-card-duplicate-route-container">
                                        <span className="route-prefix">bonusclub.link/</span>
                                        <input
                                            type="text"
                                            value={duplicateUsername}
                                            onChange={(e) => setDuplicateUsername(e.target.value.toLowerCase())}
                                            placeholder="username"
                                            disabled={duplicating}
                                            className="space-card-duplicate-route-input"
                                        />
                                    </div>
                                    <small className="input-hint">
                                        Only lowercase letters, numbers and hyphens
                                    </small>
                                </div>
                            </div>

                            <div className="space-card-duplicate-actions">
                                <button 
                                    className="cancel-btn"
                                    onClick={handleCancelDuplicate}
                                    disabled={duplicating}
                                >
                                    Cancel
                                </button>
                                <button 
                                    className="space-card-duplicate-btn space-card-primary-btn"
                                    onClick={handleDuplicateSpaceConfirm}
                                    disabled={!duplicateSpaceName.trim() || !duplicateUsername.trim() || duplicating}
                                >
                                    {duplicating ? (
                                        <>
                                            <i className="fas fa-spinner fa-spin"></i>
                                            Duplicating...
                                        </>
                                    ) : (
                                        <>
                                            <i className="fas fa-copy"></i>
                                            Duplicate Space
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Delete Space Modal */}
                {showDeleteModal && spaceToDelete && (
                    <div className="space-card-delete-modal-overlay">
                        <div className="space-card-delete-modal">
                            <div className="space-card-delete-header">
                                <h3>
                                    <i className="fas fa-exclamation-triangle"></i>
                                    Delete Space
                                </h3>
                                <button 
                                    className="close-modal-btn"
                                    onClick={handleCancelDelete}
                                    disabled={deleting}
                                >
                                    <i className="fas fa-times"></i>
                                </button>
                            </div>
                            
                            <div className="space-card-delete-body">
                                <div className="space-card-warning-box">
                                    <i className="fas fa-exclamation-circle"></i>
                                    <div className="space-card-warning-text">
                                        <strong>This action cannot be undone!</strong>
                                        <p>This will permanently delete:</p>
                                    </div>
                                </div>

                                <div className="space-card-deletion-list">
                                    <div className="space-card-deletion-item">
                                        <i className="fas fa-globe"></i>
                                        <span>Space: <strong>{spaceToDelete.spaceName}</strong></span>
                                    </div>
                                    <div className="space-card-deletion-item">
                                        <i className="fas fa-link"></i>
                                        <span>URL: <strong>bonusclub.link/{spaceToDelete.username}/</strong></span>
                                    </div>
                                    <div className="space-card-deletion-item">
                                        <i className="fas fa-file-alt"></i>
                                        <span><strong>{spaceToDelete.pageCount || 0}</strong> Pages and all content</span>
                                    </div>
                                    {spaceToDelete.customDomain && (
                                        <div className="space-card-deletion-item">
                                            <i className="fas fa-globe"></i>
                                            <span>Custom Domain: <strong>{spaceToDelete.customDomain}</strong></span>
                                        </div>
                                    )}
                                </div>

                                <div className="form-group space-card-confirm-input-group">
                                    <label>
                                        Type <strong>{spaceToDelete.spaceName}</strong> to confirm deletion
                                    </label>
                                    <input
                                        type="text"
                                        value={deleteConfirmText}
                                        onChange={(e) => setDeleteConfirmText(e.target.value)}
                                        placeholder={spaceToDelete.spaceName}
                                        disabled={deleting}
                                        className="space-card-confirm-input"
                                        autoFocus
                                    />
                                    {deleteConfirmText && deleteConfirmText !== spaceToDelete.spaceName && (
                                        <small className="error">
                                            ❌ Space name does not match
                                        </small>
                                    )}
                                    {deleteConfirmText === spaceToDelete.spaceName && (
                                        <small className="success">
                                            ✅ Space name confirmed
                                        </small>
                                    )}
                                </div>
                            </div>

                            <div className="space-card-delete-actions">
                                <button 
                                    className="cancel-btn"
                                    onClick={handleCancelDelete}
                                    disabled={deleting}
                                >
                                    Cancel
                                </button>
                                <button 
                                    className="space-card-delete-btn space-card-danger-btn"
                                    onClick={handleDeleteSpaceConfirm}
                                    disabled={deleteConfirmText !== spaceToDelete.spaceName || deleting}
                                >
                                    {deleting ? (
                                        <>
                                            <i className="fas fa-spinner fa-spin"></i>
                                            Deleting...
                                        </>
                                    ) : (
                                        <>
                                            <i className="fas fa-trash"></i>
                                            Delete Space Permanently
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default AddPageComponent;
